import React from 'react';
import { Box, Typography, useTheme, Grid, List, ListItem, ListItemIcon } from '@mui/material';
import { FaUtensils, FaHeart, FaThumbsUp, FaThumbsDown } from 'react-icons/fa';
import { FiberManualRecord as BulletPointIcon } from '@mui/icons-material';
import { SectionProps, CarePlanFieldPath } from '../../../../types/ndisSection/carePlanInfoTypes';
import { SectionHeader, ArrayField, CareTextField } from '../Components';

const getNestedValue = (obj: any, path: string): string => {
  if (!obj) return 'Not provided';
  const keys = path.split('.');
  let current = obj;
  for (const key of keys) {
    if (current && typeof current === 'object' && key in current) {
      current = current[key];
    } else {
      return 'Not provided';
    }
  }
  return current || 'Not provided';
};

const MealPreferencesSection: React.FC<SectionProps> = ({
  editMode,
  isMobile,
  handleEditToggle,
  handleSubmit,
  handleCancel,
  control,
  errors,
  getValues,
  carePlan,
  dislikes,
  setDislikes
}) => {
  const theme = useTheme();
  const spacing = isMobile ? 1 : 2;

  const fields: { label: string; name: CarePlanFieldPath; icon: JSX.Element }[] = [
    { label: 'Likes', name: 'MealPreferences.Likes' as CarePlanFieldPath, icon: <FaThumbsUp /> },
    {
      label: 'Dietary Requirements',
      name: 'MealPreferences.DietaryRequirements' as CarePlanFieldPath,
      icon: <FaHeart />
    },
    {
      label: 'Preferred Diet',
      name: 'MealPreferences.PreferredDiet' as CarePlanFieldPath,
      icon: <FaHeart />
    }
  ];

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
        boxShadow: theme.shadows[1],
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%',
        mx: 'auto'
      }}
    >
      <SectionHeader
        icon={<FaUtensils size={isMobile ? 16 : 20} />}
        title="Meal Preferences"
        color={theme.palette.success.main}
        editMode={editMode}
        handleEditToggle={handleEditToggle}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        getValues={getValues}
      />

      <Box sx={{ p: isMobile ? 1 : 2 }}>
        {editMode ? (
          <>
            <Grid container spacing={spacing}>
              {fields.map((field) => (
                <Grid item xs={12} key={field.name}>
                  <CareTextField
                    label={field.label}
                    name={field.name}
                    control={control}
                    errors={errors}
                    getValues={getValues}
                    icon={React.cloneElement(field.icon, { size: isMobile ? 14 : 16 })}
                    size={isMobile ? 'small' : 'medium'}
                  />
                </Grid>
              ))}
              <Grid item xs={12}>
                <ArrayField
                  name="MealPreferences.Dislikes"
                  label="Dislikes"
                  icon={<FaThumbsDown size={isMobile ? 14 : 16} />}
                  color={theme.palette.success.main}
                  items={dislikes}
                  setItems={setDislikes}
                  editMode={editMode}
                  errors={errors}
                />
              </Grid>
            </Grid>
          </>
        ) : (
          <>
            <Grid container spacing={spacing}>
              {fields.map((field) => {
                const value = getNestedValue(carePlan, field.name);
                return (
                  <Grid item xs={12} sm={6} key={field.name}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: isMobile ? 1 : 1.5,
                        backgroundColor: theme.palette.background.default,
                        borderRadius: 1,
                        height: '100%',
                        minHeight: isMobile ? 56 : 64,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover
                        }
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: theme.palette.success.light,
                          color: theme.palette.success.contrastText,
                          borderRadius: '50%',
                          width: isMobile ? 32 : 36,
                          height: isMobile ? 32 : 36,
                          flexShrink: 0
                        }}
                      >
                        {React.cloneElement(field.icon, {
                          size: isMobile ? 14 : 16
                        })}
                      </Box>
                      <Box sx={{ overflow: 'hidden', flex: 1 }}>
                        <Typography
                          variant={isMobile ? 'caption' : 'subtitle2'}
                          sx={{
                            fontWeight: 500,
                            color: theme.palette.text.secondary,
                            lineHeight: 1.2
                          }}
                        >
                          {field.label}
                        </Typography>
                        <Typography
                          variant={isMobile ? 'body2' : 'body1'}
                          sx={{
                            fontWeight: 400,
                            color: theme.palette.text.primary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}
                        >
                          {value}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: isMobile ? 1 : 2,
                    backgroundColor: theme.palette.background.default,
                    borderRadius: 1,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover
                    }
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: theme.palette.success.light,
                        color: theme.palette.success.contrastText,
                        borderRadius: '50%',
                        width: isMobile ? 32 : 36,
                        height: isMobile ? 32 : 36,
                        flexShrink: 0
                      }}
                    >
                      <FaThumbsDown size={isMobile ? 14 : 16} />
                    </Box>
                    <Typography
                      variant={isMobile ? 'subtitle2' : 'h6'}
                      sx={{
                        fontWeight: 500,
                        color: theme.palette.text.secondary
                      }}
                    >
                      Dislikes
                    </Typography>
                  </Box>

                  {dislikes?.length > 0 ? (
                    <List dense sx={{ py: 0 }}>
                      {dislikes.map((item, index) => (
                        <ListItem key={index} sx={{ py: 0.5, px: 1 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}>
                            <BulletPointIcon
                              sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary }}
                            />
                          </ListItemIcon>
                          <Typography variant="body2">{item}</Typography>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" sx={{ fontStyle: 'italic', pl: 4 }}>
                      No dislikes recorded
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </Box>
  );
};

export default MealPreferencesSection;
